import React, { useState } from 'react';
import '../styles/MobileQRGenerator.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const qrTypes = [
  { value: 'text', label: 'Plain Text', icon: '📝' },
  { value: 'url', label: 'Website URL', icon: '🔗' },
  { value: 'email', label: 'Email Address', icon: '📧' },
  { value: 'phone', label: 'Phone Number', icon: '📞' },
  { value: 'sms', label: 'SMS Message', icon: '💬' }
];

const MobileQRGenerator = ({ onGenerate }) => {
  const [formData, setFormData] = useState({
    data: '',
    qr_type: 'text',
    size: 10,
    border: 4,
    error_correction: 'M'
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedQR, setGeneratedQR] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'size' || name === 'border' ? parseInt(value) : value
    }));
    
    // Clear previous results when data changes
    if (name === 'data') {
      setGeneratedQR(null);
      setError('');
    }
  };

  const validateInput = () => {
    const { data, qr_type } = formData;
    
    if (!data.trim()) {
      return 'Please enter some content for the QR code';
    }
    
    switch (qr_type) {
      case 'url':
        if (!data.match(/^(https?:\/\/)|(www\.)|([a-zA-Z0-9-]+\.[a-zA-Z]{2,})/)) {
          return 'Please enter a valid URL';
        }
        break;
      case 'email':
        if (!data.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
          return 'Please enter a valid email address';
        }
        break;
      case 'phone':
        if (!data.match(/^[\+]?[\d\s\-\(\)]+$/)) {
          return 'Please enter a valid phone number';
        }
        break;
      default:
        break;
    }
    
    return null;
  };

  const handleGenerate = async () => {
    setError('');
    
    const validationError = validateInput();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setIsGenerating(true);
    
    try {
      const response = await fetch(`${API}/mobile/timesheets/qr-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          project_number: formData.data,
          client_name: 'Mobile Generated',
          description: `${formData.qr_type.toUpperCase()}: ${formData.data}`,
          location: '',
          estimated_hours: 8,
          assigned_manager: 'Mobile User',
          priority: 'Normal',
          start_date: new Date().toISOString().split('T')[0]
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setGeneratedQR({
          success: true,
          qr_code: result.qr_code,
          data: formData.data,
          type: formData.qr_type
        });
        onGenerate?.(result);
      } else {
        setError('Failed to generate QR code');
      }
    } catch (err) {
      setError('Network error: Unable to generate QR code');
      console.error('Generation error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShare = async () => {
    if (!generatedQR || !navigator.share) {
      // Fallback for browsers without Web Share API
      handleDownload();
      return;
    }

    try {
      // Convert base64 to blob for sharing
      const response = await fetch(generatedQR.qr_code);
      const blob = await response.blob();
      const file = new File([blob], 'qr-code.png', { type: 'image/png' });

      await navigator.share({
        title: 'QR Code',
        text: `QR Code for: ${generatedQR.data}`,
        files: [file]
      });
    } catch (err) {
      console.log('Share failed:', err);
      handleDownload();
    }
  };

  const handleDownload = () => {
    if (!generatedQR) return;

    const link = document.createElement('a');
    link.href = generatedQR.qr_code;
    link.download = `qr-code-${formData.qr_type}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyData = async () => {
    try {
      await navigator.clipboard.writeText(formData.data);
      // Show temporary feedback
      const button = document.querySelector('.copy-button');
      const originalText = button.textContent;
      button.textContent = '✓ Copied!';
      setTimeout(() => {
        button.textContent = originalText;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getPlaceholder = () => {
    switch (formData.qr_type) {
      case 'url': return 'https://example.com';
      case 'email': return 'user@example.com';
      case 'phone': return '+1234567890';
      case 'sms': return 'Hello from QR code!';
      default: return 'Enter your text here...';
    }
  };

  const clearForm = () => {
    setFormData({
      data: '',
      qr_type: 'text',
      size: 10,
      border: 4,
      error_correction: 'M'
    });
    setGeneratedQR(null);
    setError('');
  };

  return (
    <div className="mobile-generator-container">
      <div className="generator-content">
        <div className="form-section">
          {/* QR Type Selection */}
          <div className="form-group">
            <label className="form-label">QR Code Type</label>
            <div className="type-selector">
              {qrTypes.map(type => (
                <button
                  key={type.value}
                  type="button"
                  className={`type-button ${formData.qr_type === type.value ? 'active' : ''}`}
                  onClick={() => handleInputChange({ target: { name: 'qr_type', value: type.value } })}
                >
                  <span className="type-icon">{type.icon}</span>
                  <span className="type-label">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content Input */}
          <div className="form-group">
            <label className="form-label">Content</label>
            {formData.qr_type === 'text' ? (
              <textarea
                name="data"
                value={formData.data}
                onChange={handleInputChange}
                placeholder={getPlaceholder()}
                maxLength={500}
                className="form-textarea"
                rows={4}
              />
            ) : (
              <input
                type={formData.qr_type === 'email' ? 'email' : 'text'}
                name="data"
                value={formData.data}
                onChange={handleInputChange}
                placeholder={getPlaceholder()}
                maxLength={500}
                className="form-input"
              />
            )}
            <div className="character-count">
              {formData.data.length}/500 characters
            </div>
          </div>

          {/* Size Control */}
          <div className="form-group">
            <label className="form-label">Size: {formData.size}</label>
            <input
              type="range"
              name="size"
              min="5"
              max="20"
              value={formData.size}
              onChange={handleInputChange}
              className="form-range"
            />
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="button-group">
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !formData.data.trim()}
              className="generate-button"
            >
              {isGenerating ? (
                <>
                  <span className="loading-spinner"></span>
                  Generating...
                </>
              ) : (
                <>
                  <span>⚡</span>
                  Generate QR Code
                </>
              )}
            </button>

            {formData.data.trim() && (
              <button
                onClick={handleCopyData}
                className="copy-button"
              >
                📋 Copy Text
              </button>
            )}
          </div>
        </div>

        {generatedQR && (
          <div className="result-section">
            <div className="qr-preview">
              <img 
                src={generatedQR.qr_code}
                alt="Generated QR Code"
                className="qr-image"
              />
            </div>
            
            <div className="qr-info">
              <h3>QR Code Generated!</h3>
              <p className="qr-content">{generatedQR.data}</p>
              <p className="qr-type">Type: {generatedQR.type.toUpperCase()}</p>
            </div>

            <div className="action-buttons">
              {navigator.share && (
                <button onClick={handleShare} className="share-button">
                  📤 Share
                </button>
              )}
              <button onClick={handleDownload} className="download-button">
                💾 Download
              </button>
              <button onClick={clearForm} className="clear-button">
                🗑️ New QR
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileQRGenerator;